import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";

export type TaskType = {
  id: string; code: string; label: string; description: string;
  default_priority: string; supports_all_day: boolean;
  is_follow_up_type: boolean; order: number; system_default: boolean;
};

type Scope = {
  contact_id?: string; organization_id?: string; opportunity_id?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSuccess: (created: any) => void;
  scope?: Scope;
  presetTaskTypeCode?: string;
  presetFollowUp?: boolean;
  currentUserId: string;
};

function makeIdempotencyKey(): string {
  const g: any = globalThis;
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return "tk-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function nowLocalDateTime(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 60 - d.getMinutes() % 15);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
    "T" + pad(d.getHours()) + ":" + pad(d.getMinutes())
  );
}
function todayDateStr(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

export const TaskForm: React.FC<Props> = ({
  open, onOpenChange, onSuccess, scope, presetTaskTypeCode,
  presetFollowUp, currentUserId,
}) => {
  const { user } = useAuth();
  const [types, setTypes] = useState<TaskType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState<boolean>(false);
  const [tenantTz, setTenantTz] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [typeCode, setTypeCode] = useState<string>("");
  const [allDay, setAllDay] = useState<boolean>(false);
  const [dueAt, setDueAt] = useState<string>(nowLocalDateTime());
  const [dueDate, setDueDate] = useState<string>(todayDateStr());
  const [priority, setPriority] = useState<string>("normal");
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const idemRef = useRef<string>(makeIdempotencyKey());
  const scopeKey = JSON.stringify(scope || {}) + "|" + (user?.id ?? "") + "|" + (presetTaskTypeCode ?? "");

  useEffect(() => {
    // Reset opaque idempotency key on scope/session change.
    idemRef.current = makeIdempotencyKey();
  }, [scopeKey, open]);

  useEffect(() => {
    if (!open) return;
    setLoadingTypes(true);
    Promise.all([
      api.get("/tenant/task-types"),
      api.get("/tenant/settings").catch(() => ({ data: { default_timezone: "UTC" } })),
    ])
      .then(([tr, sr]) => {
        setTypes(tr.data.items || []);
        setTenantTz(sr.data?.default_timezone || "UTC");
        const desired = presetTaskTypeCode
          || (presetFollowUp ? "follow_up" : "general_task");
        const found = (tr.data.items || []).find((t: TaskType) => t.code === desired);
        setTypeCode(found?.code || (tr.data.items?.[0]?.code ?? ""));
        setPriority(found?.default_priority || "normal");
      })
      .finally(() => setLoadingTypes(false));
  }, [open, presetTaskTypeCode, presetFollowUp]);

  const selectedType = useMemo(
    () => types.find((t) => t.code === typeCode) || null,
    [types, typeCode],
  );

  const canSubmit =
    !submitting && title.trim().length > 0 && !!typeCode && !!currentUserId;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    const payload: any = {
      title: title.trim(),
      task_type_code: typeCode,
      all_day: allDay,
      priority,
      assigned_to_user_id: currentUserId,
      notes: notes || null,
    };
    if (allDay) payload.due_date = dueDate;
    else {
      // Send the tenant-local wall time + authoritative tenant tz so
      // the backend converts using zoneinfo, independent of the
      // browser's timezone. Falls back to browser-converted `due_at`
      // only if the tenant tz has not loaded yet.
      if (tenantTz) {
        payload.due_at_local = dueAt;
        payload.due_timezone = tenantTz;
      } else {
        const d = new Date(dueAt);
        payload.due_at = d.toISOString();
      }
    }
    if (scope?.contact_id) payload.contact_id = scope.contact_id;
    if (scope?.organization_id) payload.organization_id = scope.organization_id;
    if (scope?.opportunity_id) payload.opportunity_id = scope.opportunity_id;
    try {
      const r = await api.post("/crm/tasks", payload, {
        headers: { "Idempotency-Key": idemRef.current },
      });
      idemRef.current = makeIdempotencyKey();
      onSuccess(r.data);
      onOpenChange(false);
      // Reset form for next open.
      setTitle(""); setNotes("");
    } catch (e: any) {
      setError(formatApiError(e) || "Could not create task");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {presetFollowUp ? "Add Follow-Up" : "Add Task"}
          </DialogTitle>
          <DialogDescription>
            Server sets due-state (due today / overdue / upcoming) automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="task-title">Title</Label>
            <Input id="task-title" data-testid="task-title-input"
              value={title} onChange={(e) => setTitle(e.target.value)}
              maxLength={160} placeholder="What needs to happen?"
              required />
          </div>
          <div>
            <Label htmlFor="task-type">Type</Label>
            <Select value={typeCode} onValueChange={setTypeCode}>
              <SelectTrigger data-testid="task-type-select" id="task-type">
                <SelectValue placeholder={loadingTypes ? "Loading…" : "Select type"} />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.code} value={t.code}
                    data-testid={`task-type-option-${t.code}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Switch id="task-all-day" data-testid="task-all-day-switch"
              checked={allDay}
              disabled={selectedType ? !selectedType.supports_all_day : false}
              onCheckedChange={(v) => setAllDay(!!v)} />
            <Label htmlFor="task-all-day">All-day</Label>
          </div>
          {allDay ? (
            <div>
              <Label htmlFor="task-due-date">Due date</Label>
              <Input id="task-due-date" data-testid="task-due-date-input"
                type="date" value={dueDate}
                onChange={(e) => setDueDate(e.target.value)} required />
            </div>
          ) : (
            <div>
              <Label htmlFor="task-due-at">
                Due (business time
                {tenantTz ? (
                  <>
                    {" "}·{" "}
                    <span data-testid="task-form-tenant-tz">{tenantTz}</span>
                  </>
                ) : null})
              </Label>
              <Input id="task-due-at" data-testid="task-due-at-input"
                type="datetime-local" value={dueAt}
                onChange={(e) => setDueAt(e.target.value)} required />
              <div className="text-xs text-slate-500 mt-1"
                   data-testid="task-form-timed-tz-hint">
                Time is interpreted in the tenant business timezone
                {tenantTz ? <> (<span data-testid="task-form-timed-tz-hint-tz">{tenantTz}</span>)</> : null}
                {" "}by the server. Your browser timezone does not affect the stored time.
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="task-priority">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger id="task-priority" data-testid="task-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["low","normal","high","urgent"].map((p) => (
                  <SelectItem key={p} value={p}
                    data-testid={`task-priority-option-${p}`}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="task-notes">Notes</Label>
            <Textarea id="task-notes" data-testid="task-notes-input"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3} maxLength={4000} />
          </div>
          {error ? (
            <div data-testid="task-form-error" role="alert"
              className="text-sm text-red-600">{error}</div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            data-testid="task-form-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit}
            data-testid="task-form-submit-btn">
            {submitting ? "Saving…" : "Create Task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
